import { useState } from 'react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@staff-app/ui';
import { AlertTriangle, CheckCircle2, FileUp, Upload } from 'lucide-react';
import type { AmazonMarketplace } from '@staff-app/shared-types';
import {
  CURRENCY_SYMBOL,
  formatDiscount,
  MARKETPLACES,
  parseCodesCsv,
  readFileText,
  useAmazonCouponPromotions,
  useImportAmazonCouponCodes,
  useRegisterAmazonCouponPromotion,
} from '../hooks/useAmazonCoupons';

/** Mirrors CURRENCY_BY_MARKETPLACE in the service — shown, never sent. */
const CURRENCY_BY_MARKETPLACE: Record<string, string> = { us: 'usd', uk: 'gbp', de: 'eur' };

const NEW_PROMOTION = '__new__';

const fmtDate = (d?: string | null) => {
  if (!d) return '—';
  const p = new Date(d);
  return isNaN(+p) ? '—' : p.toLocaleDateString();
};

/** A numbered step heading, so the dialog reads as a sequence rather than one long form. */
function Step({ n, title, hint }: { n: number; title: string; hint?: string }) {
  return (
    <div className="flex items-baseline gap-2">
      <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-muted text-xs font-semibold text-muted-foreground">
        {n}
      </span>
      <h4 className="text-sm font-semibold">{title}</h4>
      {hint ? <span className="text-xs text-muted-foreground">{hint}</span> : null}
    </div>
  );
}

/**
 * Import claim codes. ROOT ONLY — the page does not render this for anyone else, and the resolver
 * refuses it independently.
 *
 * SHAPED AROUND SELLER CENTRAL'S TWO LEVELS, because the difference between them is half a day:
 *
 *   promotion  (Money Off · one SKU · one amount · one end date)   ~4-5 HOURS to activate
 *     └─ claim code group  (N single-use codes)                    ~1-2 MINUTES to process
 *
 * So the default action is ATTACH A CODE GROUP to a promotion that already exists; recording a new
 * promotion is a deliberate, separate choice. An earlier version asked for campaign id, end date,
 * SKU, ASIN and amount on every import, which read as an instruction to build a fresh promotion
 * each time — 4-5 hours of waiting for nothing — and let two imports against one promotion
 * disagree about when it ends. That second failure lands on the ONLY date Amazon enforces.
 *
 * LAYOUT (rewritten 2026-09-24 after the first pass read as one long undifferentiated form): two
 * numbered steps, the chosen promotion rendered as a summary CARD rather than a cramped sentence,
 * a real drop zone instead of a bare file input, and a footer line that states what is about to
 * happen — "500 codes → US · ARD-0002 · $10 off" — so the action is legible before it is taken.
 * The disabled submit says WHY it is disabled; a dead button with no reason is the thing that
 * makes a dialog feel broken.
 */
export function AmazonCouponBatchDialog({
  open,
  onOpenChange,
  onImported,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  onImported?: (count: number) => void;
}) {
  const [promotionId, setPromotionId] = useState('');
  const [marketplace, setMarketplace] = useState<AmazonMarketplace | ''>('');
  const [sku, setSku] = useState('');
  const [asin, setAsin] = useState('');
  const [campaignId, setCampaignId] = useState('');
  const [campaignEndsAt, setCampaignEndsAt] = useState('');
  const [discountAmount, setDiscountAmount] = useState('10');

  const [filename, setFilename] = useState('');
  const [codes, setCodes] = useState<string[]>([]);
  const [parseError, setParseError] = useState('');
  const [dragging, setDragging] = useState(false);

  const { data: promoData, loading: loadingPromos } = useAmazonCouponPromotions(open);
  const promotions = (promoData?.amazonCouponPromotions ?? []).filter((p) => p.live);
  const selected = promotions.find((p) => p.id === promotionId);
  const creatingNew = promotionId === NEW_PROMOTION;

  const [registerPromotion, { loading: registering, error: registerError }] =
    useRegisterAmazonCouponPromotion();
  const [importCodes, { loading: importing, error: importError }] = useImportAmazonCouponCodes();

  const reset = () => {
    setPromotionId('');
    setMarketplace('');
    setSku('');
    setAsin('');
    setCampaignId('');
    setCampaignEndsAt('');
    setDiscountAmount('10');
    setFilename('');
    setCodes([]);
    setParseError('');
    setDragging(false);
  };

  const takeFile = async (file: File | undefined) => {
    setCodes([]);
    setParseError('');
    if (!file) return;
    setFilename(file.name);
    const { codes: parsed, error } = parseCodesCsv(await readFileText(file));
    if (error) {
      setParseError(error);
      return;
    }
    setCodes(parsed);
  };

  const endsDate = campaignEndsAt ? new Date(campaignEndsAt) : null;
  const endsInFuture = !!endsDate && !isNaN(+endsDate) && endsDate.getTime() > Date.now();
  const amount = Number(discountAmount);

  const newPromotionReady =
    !!marketplace &&
    sku.trim().length > 0 &&
    asin.trim().length > 0 &&
    campaignId.trim().length > 0 &&
    endsInFuture &&
    amount > 0 &&
    amount <= 1000;

  const promotionReady = creatingNew ? newPromotionReady : !!selected;
  const busy = importing || registering;
  const canSubmit = codes.length > 0 && promotionReady && !busy;

  /** Why the button is dead. Silence here is what makes a form feel broken. */
  const blocker = !promotionId
    ? 'Choose a promotion first'
    : creatingNew && !newPromotionReady
      ? 'Fill in every promotion field'
      : codes.length === 0
        ? 'Add a claim-code CSV'
        : '';

  const target = creatingNew
    ? marketplace
      ? `${marketplace.toUpperCase()} · ${sku || '—'} · ${
          CURRENCY_SYMBOL[CURRENCY_BY_MARKETPLACE[marketplace]] ?? ''
        }${discountAmount} off`
      : ''
    : selected
      ? `${selected.marketplace.toUpperCase()} · ${selected.sku} · ${formatDiscount(
          selected.discountAmount,
          selected.currency,
        )}`
      : '';

  const submit = async () => {
    if (!canSubmit) return;
    try {
      let targetId = promotionId;
      if (creatingNew) {
        const res = await registerPromotion({
          variables: {
            input: {
              marketplace: marketplace as AmazonMarketplace,
              sku: sku.trim(),
              asin: asin.trim().toUpperCase(),
              campaignId: campaignId.trim(),
              campaignEndsAt: (endsDate as Date).toISOString(),
              discountAmount: amount,
            },
          },
        });
        const created = res.data?.registerAmazonCouponPromotion;
        if (!created) return;
        targetId = created.id;
      }
      await importCodes({
        variables: {
          input: { promotionId: targetId, sourceFilename: filename || 'codes.csv', codes },
        },
      });
      onImported?.(codes.length);
      reset();
      onOpenChange(false);
    } catch {
      // Surfaced through registerError / importError. Nothing partial is written — the server
      // wraps the group and its codes in one transaction.
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) reset();
        onOpenChange(o);
      }}
    >
      <DialogContent
        className="max-h-[90vh] overflow-y-auto sm:max-w-2xl"
        data-testid="amazon-batch-dialog"
      >
        <DialogHeader>
          <DialogTitle>{'Import claim codes'}</DialogTitle>
          <DialogDescription>
            {'Every code is created in Seller Central, never here. Open the live promotion → '}
            <strong>{'Manage Claim Codes'}</strong>
            {' → new group (1–2 min), download the CSV, and upload it below.'}
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-6">
          {/* ── Step 1 — promotion ───────────────────────────────────────────────────────── */}
          <section className="grid gap-3">
            <Step n={1} title="Which promotion" hint="the codes belong to one" />

            <Select value={promotionId} onValueChange={setPromotionId}>
              <SelectTrigger data-testid="batch-promotion">
                <SelectValue
                  placeholder={loadingPromos ? 'Loading promotions…' : 'Choose the live promotion'}
                />
              </SelectTrigger>
              <SelectContent>
                {promotions.map((p) => (
                  <SelectItem key={p.id} value={p.id}>
                    {`${p.marketplace.toUpperCase()} · ${p.sku} · ${formatDiscount(
                      p.discountAmount,
                      p.currency,
                    )} · ${p.available} left`}
                  </SelectItem>
                ))}
                <SelectItem value={NEW_PROMOTION} data-testid="batch-new-promotion">
                  {'+ Record a promotion I created in Seller Central…'}
                </SelectItem>
              </SelectContent>
            </Select>

            {/* The chosen promotion as a SUMMARY CARD. These fields are inherited, not editable —
                that is what stops a second CSV disagreeing with the first about the end date. */}
            {selected ? (
              <div
                className="rounded-md border bg-muted/30 p-3 text-xs"
                data-testid="batch-inherited"
              >
                <div className="mb-2 flex items-center gap-1.5 font-medium text-foreground">
                  <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />
                  {'Inherited from this promotion — nothing to retype'}
                </div>
                <dl className="grid grid-cols-2 gap-x-6 gap-y-1 text-muted-foreground sm:grid-cols-3">
                  <div>
                    <dt className="text-[11px] uppercase tracking-wide">{'Campaign'}</dt>
                    <dd className="font-mono text-foreground">{selected.campaignId}</dd>
                  </div>
                  <div>
                    <dt className="text-[11px] uppercase tracking-wide">{'SKU / ASIN'}</dt>
                    <dd className="font-mono text-foreground">{`${selected.sku} · ${selected.asin}`}</dd>
                  </div>
                  <div>
                    <dt className="text-[11px] uppercase tracking-wide">{'Discount'}</dt>
                    <dd className="text-foreground">
                      {formatDiscount(selected.discountAmount, selected.currency)}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-[11px] uppercase tracking-wide">{'Amazon ends'}</dt>
                    <dd className="text-foreground">{fmtDate(selected.campaignEndsAt)}</dd>
                  </div>
                  <div>
                    <dt className="text-[11px] uppercase tracking-wide">{'Codes left'}</dt>
                    <dd className="text-foreground">{selected.available}</dd>
                  </div>
                  <div>
                    <dt className="text-[11px] uppercase tracking-wide">{'CSVs so far'}</dt>
                    <dd className="text-foreground">{selected.groupCount}</dd>
                  </div>
                </dl>
              </div>
            ) : null}

            {!loadingPromos && promotions.length === 0 && !creatingNew ? (
              <p
                className="rounded-md border border-amber-500/40 bg-amber-500/5 p-3 text-xs text-amber-700"
                data-testid="batch-no-promotions"
              >
                {'No live promotion yet — record one below. Building it in Seller Central takes '}
                {'4–5 hours to activate, so do it once per marketplace and top up with code groups '}
                {'afterwards.'}
              </p>
            ) : null}

            {creatingNew ? (
              <div className="grid gap-4 rounded-md border border-amber-500/40 bg-amber-500/5 p-4">
                <p
                  className="flex items-start gap-2 text-xs text-amber-700"
                  data-testid="batch-new-warning"
                >
                  <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                  <span>
                    {'This does NOT create anything on Amazon. Build the Money Off promotion in '}
                    {'Seller Central first (4–5 hours to activate), then copy its details here so '}
                    {'the codes you import can be described correctly.'}
                  </span>
                </p>

                {/* items-start for the same reason as the allocate card: the campaign-ends cell
                    grows a conditional error line, and stretched columns would drag its
                    neighbours' controls out of line. */}
                <div className="grid items-start gap-4 sm:grid-cols-2">
                  <div className="grid gap-1.5">
                    <Label htmlFor="batch-marketplace">{'Marketplace'}</Label>
                    <Select
                      value={marketplace}
                      onValueChange={(v) => setMarketplace(v as AmazonMarketplace)}
                    >
                      <SelectTrigger id="batch-marketplace" data-testid="batch-marketplace">
                        <SelectValue placeholder="Choose" />
                      </SelectTrigger>
                      <SelectContent>
                        {MARKETPLACES.map((m) => (
                          <SelectItem key={m.value} value={m.value}>
                            {m.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="grid gap-1.5">
                    <Label htmlFor="batch-amount">{'Amount off per code'}</Label>
                    <div className="flex items-center gap-2">
                      <span
                        className="w-4 text-sm text-muted-foreground"
                        data-testid="batch-currency"
                      >
                        {marketplace ? CURRENCY_SYMBOL[CURRENCY_BY_MARKETPLACE[marketplace]] : '—'}
                      </span>
                      <Input
                        id="batch-amount"
                        type="number"
                        min={1}
                        max={1000}
                        step="0.01"
                        value={discountAmount}
                        onChange={(e) => setDiscountAmount(e.target.value)}
                        data-testid="batch-discount"
                      />
                    </div>
                  </div>

                  <div className="grid gap-1.5">
                    <Label htmlFor="batch-sku">{'SKU'}</Label>
                    <Input
                      id="batch-sku"
                      value={sku}
                      onChange={(e) => setSku(e.target.value)}
                      placeholder="ARD-0002"
                      data-testid="batch-sku"
                    />
                  </div>

                  <div className="grid gap-1.5">
                    <Label htmlFor="batch-asin">{'ASIN'}</Label>
                    <Input
                      id="batch-asin"
                      value={asin}
                      onChange={(e) => setAsin(e.target.value)}
                      placeholder="B09LRF5KCJ"
                      data-testid="batch-asin"
                    />
                  </div>

                  <div className="grid gap-1.5">
                    <Label htmlFor="batch-campaign">{'Tracking ID'}</Label>
                    <Input
                      id="batch-campaign"
                      value={campaignId}
                      onChange={(e) => setCampaignId(e.target.value)}
                      placeholder="e.g. 8f3a12c4"
                      data-testid="batch-campaign-id"
                    />
                    {/* This is the JOIN KEY for the daily redemption sync — it is what appears in
                        the order data Amazon returns. A wrong one reports zero redemptions
                        forever, which looks exactly like nobody using the codes. */}
                    <p className="text-xs text-muted-foreground">
                      {'In Seller Central, beside the promotion\u2019s Internal Description. This is '}
                      {'what Amazon reports on orders, so redemptions only sync if it matches.'}
                    </p>
                  </div>

                  <div className="grid gap-1.5">
                    <Label htmlFor="batch-ends">{'Campaign ends'}</Label>
                    <Input
                      id="batch-ends"
                      type="date"
                      value={campaignEndsAt}
                      onChange={(e) => setCampaignEndsAt(e.target.value)}
                      data-testid="batch-campaign-ends"
                    />
                    {campaignEndsAt && !endsInFuture ? (
                      <p className="text-xs text-destructive" data-testid="batch-ends-past">
                        {'That date has passed — none of these codes could ever be allocated.'}
                      </p>
                    ) : null}
                  </div>
                </div>

                <p className="text-xs text-muted-foreground">
                  {marketplace
                    ? `Denominated in ${CURRENCY_BY_MARKETPLACE[marketplace].toUpperCase()} — the ${
                        MARKETPLACES.find((m) => m.value === marketplace)?.label ?? ''
                      } store's currency. The house value is 10 in every store ($10 / £10 / €10), NOT a converted $10. `
                    : 'Currency follows the marketplace you pick above. The house value is 10 in each store’s own currency. '}
                  {'The campaign end date is the only one Amazon enforces.'}
                </p>
              </div>
            ) : null}
          </section>

          {/* ── Step 2 — the CSV ─────────────────────────────────────────────────────────── */}
          <section className="grid gap-3">
            <Step n={2} title="Claim-code CSV" hint="downloaded from Manage Claim Codes" />

            {/* A real drop zone. The bare file input this replaced gave no feedback beyond a
                filename, and no hint that the file is parsed here rather than uploaded. */}
            <label
              htmlFor="batch-file"
              onDragOver={(e) => {
                e.preventDefault();
                setDragging(true);
              }}
              onDragLeave={() => setDragging(false)}
              onDrop={(e) => {
                e.preventDefault();
                setDragging(false);
                void takeFile(e.dataTransfer.files?.[0]);
              }}
              className={[
                'flex cursor-pointer flex-col items-center justify-center gap-1 rounded-md border border-dashed p-6 text-center transition-colors',
                dragging ? 'border-primary bg-primary/5' : 'border-border hover:bg-muted/40',
                parseError ? 'border-destructive/60' : '',
              ].join(' ')}
            >
              {parseError ? (
                <AlertTriangle className="h-5 w-5 text-destructive" />
              ) : codes.length ? (
                <CheckCircle2 className="h-5 w-5 text-emerald-600" />
              ) : (
                <FileUp className="h-5 w-5 text-muted-foreground" />
              )}

              {parseError ? (
                <span className="text-xs text-destructive" data-testid="batch-parse-error">
                  {parseError}
                </span>
              ) : codes.length ? (
                <span className="text-sm" data-testid="batch-code-count">
                  <strong>{`${codes.length} code${codes.length === 1 ? '' : 's'} ready to import`}</strong>
                  {filename ? <span className="text-muted-foreground">{` — ${filename}`}</span> : null}
                </span>
              ) : (
                <span className="text-sm text-muted-foreground">
                  {'Drop the CSV here, or click to choose'}
                </span>
              )}

              <span className="text-xs text-muted-foreground">
                {codes.length || parseError
                  ? 'Click to choose a different file'
                  : 'Parsed in your browser — the file itself is never uploaded'}
              </span>

              <Input
                id="batch-file"
                type="file"
                accept=".csv,text/csv"
                className="sr-only"
                onChange={(e) => {
                  void takeFile(e.target.files?.[0]);
                }}
                data-testid="batch-file"
              />
            </label>
          </section>
        </div>

        {registerError || importError ? (
          <div
            className="rounded-md border border-destructive p-3 text-sm text-destructive"
            data-testid="batch-import-error"
          >
            {(registerError ?? importError)?.message}
          </div>
        ) : null}

        <DialogFooter className="items-center gap-3 sm:justify-between">
          {/* States what is about to happen, so the action is legible before it is taken. */}
          <p className="text-xs text-muted-foreground" data-testid="batch-summary">
            {canSubmit
              ? `Importing ${codes.length} code${codes.length === 1 ? '' : 's'} into ${target}`
              : blocker}
          </p>
          <div className="flex gap-2">
            <Button variant="ghost" onClick={() => onOpenChange(false)}>
              {'Cancel'}
            </Button>
            <Button
              onClick={() => {
                void submit();
              }}
              disabled={!canSubmit}
              className="gap-1"
              data-testid="batch-import-submit"
            >
              <Upload className="h-4 w-4" />
              {busy ? 'Importing…' : 'Import codes'}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
